import React, { useEffect, useState } from 'react';
import { ParsedLesson } from '@customtypes/parsedLesson';
import { useRouter } from 'next/router';
import SimpleArrowLeftIconComponent from '@components/icons/simpleArrowLeftIcon';
import { getSavedLessonProgressFromIDB } from '@utils/idb/idb';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { DefaultTooltip } from '@utils/tooltip-utils';
import { UUID_V4 } from '@utils/general-utils';
import * as Sentry from '@sentry/nextjs';
import { getLastCompletedLessonIndex } from '@utils/content-utils-web';
import Link from 'next/link';
import { TOOLTIP_DELAY_SHOW } from '@utils/constants';

interface ChapterProgressBarProps {
  lessons: ParsedLesson[];
  tutorialId: number;
}
export default function ChapterProgressBarComponent({
  lessons,
  tutorialId,
}: ChapterProgressBarProps) {
  const router = useRouter();

  const [lessonProgress, setLessonProgress] = useState<any>();

  const [tooltipId, setTooltipId] = useState<string | undefined>(undefined);
  const [lastCompletedLessonIndex, setLastCompletedLessonIndex] =
    useState<number>(0);

  async function getLessonProgress() {
    try {
      const savedLessonProgress = await getSavedLessonProgressFromIDB(
        Number(tutorialId),
      );
      setLessonProgress(savedLessonProgress.map((item) => item.lessonId));
      setLastCompletedLessonIndex(
        getLastCompletedLessonIndex(lessons, savedLessonProgress),
      );
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          context: 'IDBException - getLessonProgress - ChapterProgressBar.tsx',
        },
      });
    }
  }

  useEffect(() => {
    getLessonProgress();
  }, [router.query.lesson]);

  useEffect(() => {
    setTooltipId(UUID_V4());
  }, []);

  function isNavigationViaProgressBarAllowed(lessonIndex: number) {
    return (
      lessonIndex <= lastCompletedLessonIndex + 2 &&
      lessonIndex !== Number(router.query.lesson) &&
      lessonIndex > 0 &&
      lessonIndex <= lessons.length
    );
  }

  function LinkWrapper({
    children,
    lessonIndex,
    className,
  }: {
    children?: React.ReactNode;
    lessonIndex: number;
    className: string;
  }) {
    const navigationViaProgressBarAllowed =
      isNavigationViaProgressBarAllowed(lessonIndex);
    if (!navigationViaProgressBarAllowed) {
      return (
        <span className={`inline-block ${className}`}>
          {children && children}
        </span>
      );
    }

    return (
      <Link
        href={{
          pathname: router.pathname,
          query: { ...router.query, lesson: lessonIndex },
        }}
        className={className}
      >
        {children}
      </Link>
    );
  }

  const enableBackButton = Number(router.query.lesson) > 1;
  const enableForwardButton =
    Number(router.query.lesson) < lessons.length &&
    Number(router.query.lesson) < lastCompletedLessonIndex + 2;

  return (
    <>
      {tooltipId ? (
        <ReactTooltip
          className="defaultTooltip"
          id={tooltipId}
          opacity={1}
          noArrow
          place="bottom"
          delayShow={TOOLTIP_DELAY_SHOW}
        ></ReactTooltip>
      ) : null}
      <div className="flex flex-grow-[2] items-center justify-center space-x-2">
        <span
          className="flex"
          {...(enableBackButton
            ? {
                'data-tooltip-id': tooltipId,
                'data-tooltip-html': DefaultTooltip(`Previous exercise`),
              }
            : {})}
        >
          <LinkWrapper
            lessonIndex={Number(router.query.lesson) - 1}
            className={`rounded-lg p-1 ${
              enableBackButton
                ? 'cursor-pointer text-product2-content-primary hover:bg-product2-background-tertiary '
                : 'cursor-not-allowed text-product2-content-disabled'
            }`}
          >
            <SimpleArrowLeftIconComponent className="h-6 w-6" />
          </LinkWrapper>
        </span>
        <div className="relative flex h-1.5 flex-grow gap-1 overflow-hidden">
          {lessons.map((lesson, index) => {
            const completed = lessonProgress?.find(
              (lessonProgressItem: number) => lessonProgressItem === lesson.id,
            );
            const previousCompleted = lessonProgress?.find(
              (lessonProgressItem: number) =>
                lessonProgressItem === lessons[index - 1]?.id,
            );
            const currentLessonIndex = Number(router.query.lesson) - 1;
            const before = index < currentLessonIndex;
            const current = index === currentLessonIndex;
            const pillClass = current
              ? 'bg-product2-background-accentLight cursor-default'
              : before
                ? 'bg-product2-background-accentLight'
                : completed || previousCompleted
                  ? 'bg-product2-background-accent'
                  : 'bg-product-progress-empty-dark cursor-default';

            return (
              <span
                key={lesson.id}
                className="flex grow"
                data-tooltip-id={tooltipId}
                data-tooltip-html={DefaultTooltip(`Exercise ${index + 1}`)}
              >
                <LinkWrapper
                  lessonIndex={index + 1}
                  className={`${pillClass} grow rounded-lg border-2 border-transparent`}
                ></LinkWrapper>
              </span>
            );
          })}
        </div>
        <span
          className="ml-4 flex"
          {...(enableForwardButton
            ? {
                'data-tooltip-id': tooltipId,
                'data-tooltip-html': DefaultTooltip(`Next exercise`),
              }
            : {})}
        >
          <LinkWrapper
            lessonIndex={Number(router.query.lesson) + 1}
            className={`rounded-xl p-1 ${
              enableForwardButton
                ? 'cursor-pointer text-product2-content-primary hover:bg-product2-background-tertiary'
                : 'cursor-not-allowed text-product2-content-disabled'
            }`}
          >
            <SimpleArrowLeftIconComponent className=" h-6 w-6 rotate-180" />
          </LinkWrapper>
        </span>
      </div>
    </>
  );
}
